import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useForm} from "react-hook-form";
import {Autocomplete, FormLabel, Grid} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React from "react"
import Box from "@mui/material/Box";
import {useAllDict} from "@/store/dict.c";
import {DictPO} from "@/model/system";
import {useDrawerModal, useGlobalActions} from "@/store/global.c";
import {useCreateLeave, useQueryLeave, useUpdateLeave} from "@/service/leave.s";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {checkQueryKey, emptyValue, filterDicts, getSelectValues, iso2Date, setValues} from "@/helper/method";
import {TagOut} from "@/widget/TagOut/TagOut";
import {DatePickers} from "@/theme/DatePickers";
import DatePicker from "react-datepicker";
import {useAuthInfo} from "@/store/account.c";

const validationSchema = z.object({
    id: z.number().nullable().default(0),
    instanceId: z.number().default(0),
    executeId: z.unknown(),
    reason: z.string({message: "请填写请假原由"}),
    categoryIds: z.string({message: "请选择请假类别"}),
    tempCategories: z.array(z.object({label: z.string(), value: z.number()})),
    categories: z.string().optional(),
    startTime: z.date({message: "请选择开始时间"}),
    deptName: z.unknown(),
    endTime: z.date({message: "请选择结束时间"}).or(z.literal(null)),
});

export type WriteLeaveForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

export const WriteLeave = () => {
    const allDict = useAllDict()
    const {leaveCategory} = allDict
    const {write, id: drawerId, execute} = useDrawerModal()
    const {setToast} = useGlobalActions()
    const {data: leaveData} = useQueryLeave({id: drawerId!}, checkQueryKey(drawerId))
    const {mutateAsync: createLeaveMutate, isPending: createLeavePending} = useCreateLeave()
    const {mutateAsync: updateLeaveMutate, isPending: updateLeavePending} = useUpdateLeave()
    const leave = leaveData?.data
    const {onSearch} = useDrawerModal()
    const {setDrawerModal} = useGlobalActions()
    const {dept: {deptName}} = useAuthInfo()

    const initValues = () => {
        if (write == 'edit') {
            return {
                ...leave, startTime: iso2Date(leave?.startTime), endTime: iso2Date(leave?.endTime),
                tempCategories: filterDicts(leaveCategory, leave?.categoryIds), deptName
            }
        }
        return {deptName}
    }
    const {
        register,
        control,
        setValue,
        reset,
        handleSubmit,
        formState: {errors},
    } = useForm<WriteLeaveForm>({
        resolver: zodResolver(validationSchema),
        values: initValues() as WriteLeaveForm,
        defaultValues: {...defaultValues},
    });

    const onSubmit = async (values: WriteLeaveForm) => {
        const result = write == 'add' ? await createLeaveMutate(values) : await updateLeaveMutate({...values, ...execute, id: drawerId!})
        if (result.code == 0) {
            onSearch && onSearch()
            setDrawerModal({open: false})
            setToast({open: true, message: result.msg, severity: "success", variant: 'standard'})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>请假类别</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <Autocomplete
                                multiple={true}
                                value={value || []}
                                slotProps={{chip: {size: 'small',}}}
                                options={leaveCategory}
                                disableListWrap={true}
                                renderValue={(values) => {
                                    return values?.map((value: DictPO, index: number) => <TagOut key={index} index={index} label={value.label}/>)
                                }}
                                onChange={(_, options) => {
                                    const values = getSelectValues(options, {key: "label", val: 'value'}, {key: "categoryIds", val: "categories"})
                                    setValues(values, setValue as any)
                                    onChange(options)
                                }}
                                disableCloseOnSelect={true}
                                renderInput={(params) => (
                                    <TextField error={!!errors?.categoryIds}
                                               sx={{"& .MuiOutlinedInput-root": {height: 'auto', minHeight: '2.5rem'}}}
                                               helperText={errors?.categoryIds?.message} placeholder={"选择请假类别"} {...params} size={'small'}/>
                                )}
                            />
                        }} name={"tempCategories"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel>所在部门</FormLabel>
                        <TextField
                            {...register("deptName")}
                            slotProps={{
                                input: {
                                    readOnly: true,
                                },
                            }}
                            placeholder="所在部门"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel>开始时间</FormLabel>
                        <DatePickers>
                            <Controller control={control} render={({field: {onChange, value}}) => {
                                return <DatePicker
                                    selected={value as any}
                                    dateFormat="yyyy/MM/dd HH:mm"
                                    timeInputLabel="时间"
                                    showTimeInput={true}
                                    onChange={(date: Date | null) => {
                                        onChange(date)
                                    }}
                                    locale='zhCN'
                                    placeholderText={"选择开始时间"}
                                    customInput={<TextField fullWidth={true}
                                                            error={!!errors?.startTime}
                                                            helperText={errors?.startTime?.message}
                                                            sx={{
                                                                "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                                            }}
                                    />}
                                />
                            }} name={'startTime'}/>
                        </DatePickers>
                    </FormControl>
                </Grid>

                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel>结束时间</FormLabel>
                        <DatePickers>
                            <Controller control={control} render={({field: {onChange, value}}) => {
                                return <DatePicker
                                    selected={value as any}
                                    dateFormat="yyyy/MM/dd HH:mm"
                                    timeInputLabel="时间"
                                    showTimeInput={true}
                                    onChange={(date: Date | null) => {
                                        onChange(date)
                                    }}
                                    locale='zhCN'
                                    placeholderText={"选择结束时间"}
                                    customInput={<TextField fullWidth={true}
                                                            error={!!errors?.endTime}
                                                            helperText={errors?.endTime?.message}
                                                            sx={{
                                                                "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                                            }}
                                    />}
                                />
                            }} name={'endTime'}/>
                        </DatePickers>
                    </FormControl>
                </Grid>
                {/*<Grid size={6}>*/}
                {/*    <FormControl fullWidth={true}>*/}
                {/*        <FormLabel>请假时间</FormLabel>*/}
                {/*        <DatePickers>*/}
                {/*            <Controller control={control} render={({field: {onChange, value}}) => {*/}
                {/*                const [startDate, endDate] = value || []*/}
                {/*                return <DatePicker*/}
                {/*                    showYearDropdown={true}*/}
                {/*                    selectsRange={true}*/}
                {/*                    showMonthDropdown={true}*/}
                {/*                    startDate={offsetDate(startDate)}*/}
                {/*                    endDate={offsetDate(endDate)}*/}
                {/*                    dateFormat="yyyy/MM/dd HH:mm"*/}
                {/*                    timeInputLabel="时间"*/}
                {/*                    showTimeInput={true}*/}
                {/*                    onChange={(date: [Date | null, Date | null]) => {*/}
                {/*                        const [startTime, endTime] = date*/}
                {/*                        setValue("startTime", startTime)*/}
                {/*                        setValue("endTime", endTime)*/}
                {/*                        onChange(date)*/}
                {/*                    }}*/}
                {/*                    locale='zhCN'*/}
                {/*                    placeholderText={"选择请假时间"}*/}
                {/*                    customInput={<TextField fullWidth={true} sx={{*/}
                {/*                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},*/}
                {/*                    }}*/}
                {/*                    />}*/}
                {/*                />*/}
                {/*            }} name={'leaveTime'}/>*/}
                {/*        </DatePickers>*/}
                {/*    </FormControl>*/}
                {/*</Grid>*/}
                <Grid size={12}>
                    <FormControl fullWidth={true}>
                        <FormLabel>请假原由</FormLabel>
                        <TextField
                            multiline
                            rows={3}
                            error={!!errors?.reason}
                            helperText={errors?.reason?.message}
                            {...register("reason")}
                            slotProps={{
                                input: {
                                    sx: {height: 80},
                                    inputProps: {maxLength: 250},
                                },
                            }}
                            placeholder="输入请假原由"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <DrawerFooter loading={createLeavePending || updateLeavePending} onReset={() => {
                reset(defaultValues)
            }}/>
        </Box>
    );
};
